import { Layout } from '../components/Layout';
import { Shield } from 'lucide-react';
import { Footer } from '../components/Footer';
import { Helmet } from 'react-helmet-async';

export function Privacy() {
  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy | CTC Calculator</title>
        <meta
          name="description"
          content="Privacy Policy for CTC Calculator. Learn how we handle your data and our use of Google AdSense cookies."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.ctccalculator.in/privacy" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-indigo-600 mr-3" aria-hidden="true" />
            <h1 className="text-3xl font-bold text-gray-800">Privacy Policy</h1>
          </div>

          <div className="prose prose-indigo max-w-none">
            <p className="text-gray-600 mb-6" aria-label="Last updated date">
              Last updated: October 3, 2026
            </p>

            <section className="mb-8" aria-labelledby="info-collect">
              <h2 id="info-collect" className="text-xl font-semibold text-gray-800 mb-4">Information We Collect</h2>
              <p className="text-gray-600 mb-4">
                We collect information that you voluntarily provide to us when you use our CTC Calculator,
                including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Salary information entered into the calculator (processed locally/in-memory, not stored on our servers)</li>
                <li>Contact information when you reach out to us via email or support channels</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="cookies-adsense">
              <h2 id="cookies-adsense" className="text-xl font-semibold text-gray-800 mb-4">Cookies and Google AdSense Advertising</h2>
              <p className="text-gray-600 mb-4">
                This website uses Google AdSense to display advertisements. To help serve personalized ads, cookies are placed on your device.
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                <li>
                  Google, as a third-party vendor, uses cookies to serve ads on our site.
                </li>
                <li>
                  Google's use of advertising cookies (such as the DoubleClick cookie) enables it and its partners to serve ads to our users based on their visit to our site and/or other sites on the Internet.
                </li>
                <li>
                  We also utilize Google Funding Choices to present a consent dialog for cookie storage and ad personalization to users located in the European Economic Area (EEA) and the United Kingdom.
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                You can manage or opt-out of these technologies through the following methods:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>
                  You can opt-out of personalized advertising by visiting the{' '}
                  <a 
                    href="https://www.google.com/settings/ads" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-indigo-600 hover:text-indigo-700 underline"
                  >
                    Google Ads Settings
                  </a>.
                </li>
                <li>
                  Alternatively, you can opt-out of a third-party vendor's use of cookies for personalized advertising by visiting{' '}
                  <a 
                    href="https://www.aboutads.info/choices" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-indigo-600 hover:text-indigo-700 underline"
                  >
                    www.aboutads.info
                  </a>.
                </li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="info-use">
              <h2 id="info-use" className="text-xl font-semibold text-gray-800 mb-4">How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>To provide and maintain our calculator service</li>
                <li>To improve and optimize website performance and user experience</li>
                <li>To respond to your inquiries and support requests</li>
                <li>To ensure security and prevent fraudulent activity</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="data-security">
              <h2 id="data-security" className="text-xl font-semibold text-gray-800 mb-4">Data Security</h2>
              <p className="text-gray-600">
                We implement appropriate security measures to protect your personal information.
                However, please note that no method of transmission over the internet or electronic
                storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8" aria-labelledby="consent-compliance">
              <h2 id="consent-compliance" className="text-xl font-semibold text-gray-800 mb-4">GDPR and CCPA Compliance</h2>
              <p className="text-gray-600 mb-4">
                If you are a resident of the European Economic Area (EEA), the United Kingdom, or California, you have specific data protection rights under the General Data Protection Regulation (GDPR) and California Consumer Privacy Act (CCPA).
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>You have the right to request access, correction, or deletion of your personal data held by us.</li>
                <li>You have the right to object to or restrict processing of your data.</li>
                <li>Since salary calculations are processed entirely client-side, we do not store, log, or maintain any of your calculated income information.</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="your-rights">
              <h2 id="your-rights" className="text-xl font-semibold text-gray-800 mb-4">Your Rights</h2>
              <p className="text-gray-600 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to data processing</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section aria-labelledby="contact-us">
              <h2 id="contact-us" className="text-xl font-semibold text-gray-800 mb-4">Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about this Privacy Policy, please contact us at{' '}
                <a 
                  href="mailto:ksanjeev284@gmail.com" 
                  className="text-indigo-600 hover:text-indigo-700 underline"
                  aria-label="Email us at ksanjeev284@gmail.com"
                >
                  ksanjeev284@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
      
      <div className="mt-8">
        <Footer />
      </div>
    </Layout>
  );
}
